import { useState, useRef, useCallback } from 'react'
import { validateStoryYaml, importStory, validateStoryOnServer } from '../services/storyApi'
import type { StoryValidationResult, StoryImportResult, ServerValidationResult } from '../services/storyApi'
import { useUser } from '../context/UserContext'
import styles from './StoryUploadPage.module.css'

export default function StoryUploadPage() {
  const { userApiKey, isLoggedIn } = useUser()
  const [yaml, setYaml] = useState('')
  const [fileName, setFileName] = useState<string | null>(null)
  const [dragOver, setDragOver] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [imported, setImported] = useState<StoryImportResult | null>(null)
  const [backendError, setBackendError] = useState<string | null>(null)
  const [validatingOnServer, setValidatingOnServer] = useState(false)
  const [serverValidation, setServerValidation] = useState<ServerValidationResult | null>(null)
  const [serverValidateError, setServerValidateError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const validation: StoryValidationResult | null = yaml.trim() ? validateStoryYaml(yaml) : null

  function resetResults() {
    setImported(null)
    setBackendError(null)
    setServerValidation(null)
    setServerValidateError(null)
  }

  function loadFile(file: File) {
    if (!file) return
    setFileName(file.name)
    resetResults()
    const reader = new FileReader()
    reader.onload = (e) => setYaml((e.target?.result as string) ?? '')
    reader.readAsText(file)
  }

  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    const file = e.dataTransfer.files[0]
    if (file) loadFile(file)
  }, [])

  async function handleValidateOnServer() {
    if (!yaml.trim() || !isLoggedIn) return
    setValidatingOnServer(true)
    setServerValidation(null)
    setServerValidateError(null)
    try {
      const result = await validateStoryOnServer(yaml, userApiKey)
      setServerValidation(result)
    } catch (e) {
      setServerValidateError((e as Error).message)
    } finally {
      setValidatingOnServer(false)
    }
  }

  async function handleUpload() {
    if (!validation?.valid || !isLoggedIn) return
    setUploading(true)
    setBackendError(null)
    setImported(null)
    try {
      const result = await importStory(yaml, userApiKey)
      setImported(result)
    } catch (e) {
      setBackendError((e as Error).message)
    } finally {
      setUploading(false)
    }
  }

  function handleTextareaChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
    setYaml(e.target.value)
    setFileName(null)
    resetResults()
  }

  const canUpload = !!validation?.valid && !uploading && isLoggedIn
  const canValidateOnServer = !!yaml.trim() && !validatingOnServer && isLoggedIn

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1 className={styles.title}>Story Upload</h1>
      </div>

      <div className={styles.content}>
        {!isLoggedIn && (
          <div className={styles.errorPanel}>
            <strong>Sign in required:</strong> connect with an API key on the Home page to import stories.
          </div>
        )}

        <div
          className={`${styles.dropZone} ${dragOver ? styles.dropZoneActive : ''}`}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".yaml,.yml"
            className={styles.fileInput}
            onChange={(e) => { const f = e.target.files?.[0]; if (f) loadFile(f) }}
          />
          {fileName
            ? <span className={styles.fileName}>{fileName}</span>
            : <>
                <span className={styles.dropText}>Drop a <code>.yaml</code> file here</span>
                <span className={styles.browseBtn}>or browse</span>
              </>
          }
        </div>

        <div className={styles.divider}>or paste YAML</div>

        <textarea
          className={styles.yamlInput}
          value={yaml}
          onChange={handleTextareaChange}
          placeholder="story:&#10;  id: my-story&#10;  ..."
          spellCheck={false}
        />

        {validation && !validation.valid && (
          <div className={styles.validationErrors}>
            <strong>Client-side validation:</strong>
            <ul>
              {validation.errors.map((err, i) => <li key={i}>{err}</li>)}
            </ul>
          </div>
        )}

        {validation?.valid && validation.summary && (
          <div className={styles.previewPanel}>
            <div className={styles.previewTitle}>Story preview</div>
            <div className={styles.previewRow}><span className={styles.previewLabel}>ID</span><span>{validation.summary.id}</span></div>
            {validation.summary.title && <div className={styles.previewRow}><span className={styles.previewLabel}>Title</span><span>{validation.summary.title}</span></div>}
            <div className={styles.previewRow}><span className={styles.previewLabel}>Character</span><span>{validation.summary.character}</span></div>
            <div className={styles.previewRow}><span className={styles.previewLabel}>Start phase</span><span>{validation.summary.startPhase}</span></div>
            <div className={styles.previewRow}><span className={styles.previewLabel}>Phases</span><span>{validation.summary.phaseCount}</span></div>
          </div>
        )}

        <div className={styles.actionRow}>
          <button
            className={styles.validateBtn}
            onClick={handleValidateOnServer}
            disabled={!canValidateOnServer}
            title="Run the server-side validator without persisting"
          >
            {validatingOnServer ? 'Validating…' : 'Validate'}
          </button>
          <button
            className={styles.uploadBtn}
            onClick={handleUpload}
            disabled={!canUpload}
          >
            {uploading ? 'Importing…' : 'Import Story'}
          </button>
        </div>

        {serverValidateError && (
          <div className={styles.errorPanel}>
            <strong>Validation request failed:</strong> {serverValidateError}
          </div>
        )}

        {serverValidation && (
          <div className={serverValidation.valid ? styles.successPanel : styles.validationErrors}>
            <div className={styles.previewTitle}>
              Server validation: {serverValidation.valid ? 'valid' : `${serverValidation.errors.length} issue${serverValidation.errors.length === 1 ? '' : 's'}`}
            </div>
            {!serverValidation.valid && serverValidation.errors.length > 0 && (
              <ul className={styles.issueList}>
                {serverValidation.errors.map((err, i) => (
                  <li key={i}>
                    <code className={styles.issuePath}>{err.path}</code>
                    {err.line ? <span className={styles.issueLine}>line {err.line}</span> : null}
                    <span className={styles.issueMessage}>{err.message}</span>
                  </li>
                ))}
              </ul>
            )}
            {serverValidation.summary && (
              <div className={styles.serverSummary}>
                {serverValidation.summary.id && <div className={styles.previewRow}><span className={styles.previewLabel}>ID</span><span>{serverValidation.summary.id}</span></div>}
                {serverValidation.summary.title && <div className={styles.previewRow}><span className={styles.previewLabel}>Title</span><span>{serverValidation.summary.title}</span></div>}
                {serverValidation.summary.version && <div className={styles.previewRow}><span className={styles.previewLabel}>Version</span><span>{serverValidation.summary.version}</span></div>}
                {serverValidation.summary.character && <div className={styles.previewRow}><span className={styles.previewLabel}>Character</span><span>{serverValidation.summary.character}</span></div>}
                {serverValidation.summary.startPhase && <div className={styles.previewRow}><span className={styles.previewLabel}>Start phase</span><span>{serverValidation.summary.startPhase}</span></div>}
                <div className={styles.previewRow}><span className={styles.previewLabel}>Phases</span><span>{serverValidation.summary.phaseCount}</span></div>
                <div className={styles.previewRow}><span className={styles.previewLabel}>Canned moments</span><span>{serverValidation.summary.cannedMomentCount}</span></div>
                {serverValidation.summary.specVersion && <div className={styles.previewRow}><span className={styles.previewLabel}>Spec version</span><span>{serverValidation.summary.specVersion}</span></div>}
              </div>
            )}
          </div>
        )}

        {backendError && (
          <div className={styles.errorPanel}>
            <strong>Import failed:</strong> {backendError}
          </div>
        )}

        {imported && (
          <div className={styles.successPanel}>
            <div className={styles.successTitle}>Story imported</div>
            <div className={styles.previewRow}><span className={styles.previewLabel}>ID</span><span>{imported.id}</span></div>
            {imported.title && <div className={styles.previewRow}><span className={styles.previewLabel}>Title</span><span>{imported.title}</span></div>}
            <div className={styles.previewRow}><span className={styles.previewLabel}>Version</span><span>{imported.version}</span></div>
            <div className={styles.previewRow}><span className={styles.previewLabel}>Character</span><span>{imported.characterName} v{imported.characterVersion}</span></div>
            <div className={styles.previewRow}><span className={styles.previewLabel}>Source filename</span><code className={styles.sessionId}>{imported.sourceFilename}</code></div>
            <a
              className={styles.startBtn}
              href={`?character=${encodeURIComponent(imported.characterName)}&story=${encodeURIComponent(imported.sourceFilename)}#chat`}
            >
              Start Chat
            </a>
          </div>
        )}
      </div>
    </div>
  )
}
